import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserCog, Trash2, KeyRound, Users, Bot, RefreshCw, AlertTriangle } from "lucide-react";
import { useUser, useUsers } from "@/api/queries/users";
import { useAgents } from "@/api/queries/agents";
import { useDeleteUser } from "@/api/mutations/users";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { AgentMiniTable } from "@/components/AgentMiniTable";
import { relativeTime } from "@/lib/relativeTime";
import { ApiError } from "@/api/client";
import { SetSupervisorDialog } from "@/components/user-actions/SetSupervisorDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ResetPasswordDialog } from "@/components/user-actions/ResetPasswordDialog";
import { OneTimeTokenDialog } from "@/components/OneTimeTokenDialog";
import { BindingsSection } from "@/components/binding-actions/BindingsSection";
import { UserRolesSection } from "@/components/user-roles/UserRolesSection";
import { UserBudgetSection } from "@/components/user-budget/UserBudgetSection";

export function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useUser(id);
  const usersQuery = useUsers();
  const [supOpen, setSupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // #199: Volumes der eigenen Agents vor dem Löschen als tar.gz sichern (Default an).
  const [archiveOwnedAgents, setArchiveOwnedAgents] = useState(true);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);

  const deleteMutation = useDeleteUser();

  // #redesign Welle B (#4): eigene Agents dieses Users (Owner) — Cross-Link.
  const agentsQuery = useAgents();
  const ownedAgents = (agentsQuery.data ?? []).filter((a) => a.ownerId === id);

  const errorMessage =
    error instanceof ApiError && error.status === 404 ? `User "${id}" nicht gefunden` : (error as Error | undefined)?.message;

  const supervisor = data?.supervisorId ? (usersQuery.data ?? []).find((u) => u.id === data.supervisorId) : null;
  const supervisorCell =
    !data?.supervisorId ? (
      <span className="text-text3">—</span>
    ) : supervisor ? (
      <Link to={`/users/${data.supervisorId}`} className="mono text-accent-text hover:underline">{supervisor.email}</Link>
    ) : (
      <Badge variant="orphaned">orphaned: {data.supervisorId}</Badge>
    );

  function handleDelete() {
    if (!data) return;
    setDeleteError(null);
    deleteMutation.mutate({ id: data.id, archive: archiveOwnedAgents }, {
      onSuccess: () => {
        toast.success(`User ${data.email} gelöscht`);
        setDeleteOpen(false);
        navigate("/users");
      },
      onError: (err) => setDeleteError((err as Error).message),
    });
  }

  // ----- Loading / Error -----
  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt User…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/users" label="Users" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />Retry</Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/users"
        backLabel="Users"
        icon={Users}
        title={data.email}
        mono
        badge={<Badge variant={data.role === "admin" ? "enrolled" : "default"}>{data.role}</Badge>}
        actions={
          <div className="flex flex-wrap gap-2" data-testid="user-actions">
            <Button variant="outline" size="sm" onClick={() => setSupOpen(true)} data-testid="user-action-supervisor">
              <UserCog className="h-4 w-4" />Set Supervisor
            </Button>
            <Button variant="outline" size="sm" onClick={() => setResetConfirmOpen(true)} data-testid="user-action-reset-password">
              <KeyRound className="h-4 w-4" />Reset Password
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="user-action-delete">
              <Trash2 className="h-4 w-4" />Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-[18px]">
          <SectionCard
            testId="user-agents-section"
            icon={<Bot className="h-[18px] w-[18px]" />}
            title={`Agents (${ownedAgents.length})`}
            description="Persönliche Agents dieses Users (Owner)."
          >
            <AgentMiniTable agents={ownedAgents} emptyText="Dieser User besitzt keine Agents." />
          </SectionCard>

          <UserRolesSection userId={data.id} />

          <BindingsSection userId={data.id} />

          <UserBudgetSection user={data} />
        </div>

        <PropGrid
          rows={[
            ["ID", <span className="mono">{data.id}</span>],
            ["Email", data.email],
            ["Role", <Badge variant={data.role === "admin" ? "enrolled" : "default"}>{data.role}</Badge>],
            // #214/#215 (B5-T2): Gitea-Owner-Login (Repo-Zugriff auf eigene Agents).
            ["Gitea-Owner-Login", data.giteaUsername
              ? <span className="mono" data-testid="gitea-owner-username">{data.giteaUsername}</span>
              : <span className="text-text3">— (keine Gitea-Anbindung)</span>],
            ["Supervisor", supervisorCell],
            ["Default-Notification-Binding", data.defaultNotificationBindingId
              ? <span className="mono text-xs">{data.defaultNotificationBindingId}</span>
              : <span className="text-text3">—</span>],
            ["Erstellt", <span className="text-text2">{relativeTime(data.createdAt)}</span>],
          ]}
        />
      </div>

      {supOpen && <SetSupervisorDialog open onOpenChange={setSupOpen} user={data} />}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="User endgültig löschen"
        description="Diese Aktion ist NICHT umkehrbar. Hard-Delete inkl. Cascade: ALLE eigenen Agents des Users (inkl. Volumes), Subordinates verlieren ihren Supervisor, alle ChannelBindings werden gelöscht."
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        requiredTypeText={data.email}
        onConfirm={handleDelete}
      >
        <label className="flex items-center gap-2 text-sm text-text2">
          <input
            type="checkbox"
            checked={archiveOwnedAgents}
            onChange={(e) => setArchiveOwnedAgents(e.target.checked)}
            data-testid="archive-checkbox"
          />
          Volumes der eigenen Agents vorher als Archiv (tar.gz) sichern
        </label>
      </ConfirmDialog>
      {data && (
        <ResetPasswordDialog
          open={resetConfirmOpen}
          onOpenChange={setResetConfirmOpen}
          userId={data.id}
          email={data.email}
          onDone={setResetResult}
        />
      )}
      {resetResult && (
        <OneTimeTokenDialog
          open
          onOpenChange={(o) => !o && setResetResult(null)}
          title={`Neues Passwort für ${resetResult.email}`}
          description="JETZT kopieren — nach Close kann das Passwort nicht mehr abgerufen werden."
          label="Passwort"
          token={resetResult.password}
        />
      )}
    </div>
  );
}
